import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IPermission } from '../../../core/models/auth.models';
import { ICampaign } from '../../../core/models/campaigns.model';
import { ILead } from '../../../core/models/lead.models';
import { PaymentMethodApiService } from '../../../pages/my-account/payment-method/payment-method.apiservice';
import { AppService } from '../../../store/app.service';
import { PAGE_NAMES } from '../../../core/helpers/constants';
import { SubscriptionApiService } from '../../../pages/subscriptions/subscription.apiservice';
import { Country, State } from 'country-state-city';

@Component({
  selector: 'modal-add-payment-method',
  templateUrl: './add-payment-method.component.html',
  styleUrls: ['./add-payment-method.component.scss']
})
export class AddPaymentMethodModal implements OnDestroy {

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Output() oSaveClicked = new EventEmitter<any>();
  doWhat: IPermission = {} as IPermission;
  allCampaigns: Array<ICampaign> = new Array<ICampaign>();
  allSubscriptions: Array<any> = new Array<any>();

  addForm!: FormGroup;
  submitted = false;
  id: string = '';
  allClients: Array<any> = new Array<any>();
  @Input() subscriptionId = '';
  @Input() clientId = '';
  @Input() isShopOrder = false;
  @Input() iSubscriptions: any[] = [];

  countries: any[] = [];
  states: any[] = [];

  @Input() set iId(value: string) {
    this.id = value;
    if (value) {
      this.loadDetails();
    }
  }
  expiryControl = new FormControl();

  constructor(
    private appService: AppService,
    private paymentMethodApiService: PaymentMethodApiService,
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private subscriptionApiService: SubscriptionApiService) {

    this.addForm = this.formBuilder.group({
      id: [null],
      paymentType: ['Card', [Validators.required]],
      businessName: [null],
      nickname: [null, [Validators.required]],
      clientId: [null, [Validators.required]],
      cardNumber: [null, [Validators.required]],
      cvv: [null, [Validators.required]],
      expiry: [null, [Validators.required]],
      achAccountName: [null],
      bankRoutingNumber: [null],
      bankAccountNumber: [null],
      firstName: [null, [Validators.required]],
      lastName: [null, [Validators.required]],
      address: [null, [Validators.required]],
      city: [null, [Validators.required]],
      stateCode: [null, [Validators.required]],
      zip: [null, [Validators.required]],
      countryCode:['US', [Validators.required]],
      subscriptionId: [null]
    });

    this.appService.getAllClientsFeed$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.allClients = [...response].map(i => { i.fullName = i.firstName + ' ' + i.lastName; return i; });
        if (this.allClients.length === 0)
          this.appService.fetchAllClientsFeed();
      });

    this.addForm.get('clientId')?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(clientId => {
        if (clientId) {
          this.loadSubscriptionsByClientId();
        }
      });
  }

  setFormValidation(type:string){
    if(type == 'Card'){
      this.addForm.controls["cardNumber"].setValidators([Validators.required]);
      this.addForm.controls["cardNumber"].updateValueAndValidity();
      // this.addForm.controls["cvv"].setValidators([Validators.required]);
      // this.addForm.controls["cvv"].updateValueAndValidity();
      this.addForm.controls["expiry"].setValidators([Validators.required]);
      this.addForm.controls["expiry"].updateValueAndValidity();
      this.addForm.controls["firstName"].setValidators([Validators.required]);
      this.addForm.controls["firstName"].updateValueAndValidity();
      this.addForm.controls["lastName"].setValidators([Validators.required]);
      this.addForm.controls["lastName"].updateValueAndValidity();
      this.addForm.controls["achAccountName"].clearValidators();
      this.addForm.controls["achAccountName"].updateValueAndValidity();
      this.addForm.controls["bankRoutingNumber"].clearValidators();
      this.addForm.controls["bankRoutingNumber"].updateValueAndValidity();
      this.addForm.controls["bankAccountNumber"].clearValidators();
      this.addForm.controls["bankAccountNumber"].updateValueAndValidity();
    }
    else{
      this.addForm.controls["cardNumber"].clearValidators();
      this.addForm.controls["cardNumber"].updateValueAndValidity();
      this.addForm.controls["cvv"].clearValidators();
      this.addForm.controls["cvv"].updateValueAndValidity();
      this.addForm.controls["expiry"].clearValidators();
      this.addForm.controls["expiry"].updateValueAndValidity();
      this.addForm.controls["firstName"].clearValidators();
      this.addForm.controls["firstName"].updateValueAndValidity();
      this.addForm.controls["lastName"].clearValidators();
      this.addForm.controls["lastName"].updateValueAndValidity();
      this.addForm.controls["achAccountName"].setValidators([Validators.required]);
      this.addForm.controls["achAccountName"].updateValueAndValidity();
      this.addForm.controls["bankRoutingNumber"].setValidators([Validators.required]);
      this.addForm.controls["bankRoutingNumber"].updateValueAndValidity();
      this.addForm.controls["bankAccountNumber"].setValidators([Validators.required]);
      this.addForm.controls["bankAccountNumber"].updateValueAndValidity();
    }
  }

  ngOnInit(): void {
    this.countries = Country.getAllCountries();
    // Default country = United States
    this.states = State.getStatesOfCountry('US');
    // this.appService.setPageTitle$('Payment Methods');
    this.doWhat = this.appService.getUserRolesForPage(PAGE_NAMES.PaymentMethods);

    // Patch the form values here
    if (this.clientId) {
      this.addForm.patchValue({ clientId: this.clientId });
    }
    if (this.subscriptionId) {
      this.addForm.patchValue({ subscriptionId: this.subscriptionId });
    }
  }

  onCountryChange(country: any): void {
    const countryCode =
        country?.isoCode ||
        this.addForm.get('countryCode')?.value;

    this.states = State.getStatesOfCountry(countryCode);

    this.addForm.patchValue({
        stateCode: null
    });
}

  get f() { return this.addForm.controls; }

  loadDetails() {
    this.addForm.patchValue({ 'id': this.id });
    this.paymentMethodApiService.getPaymentMethod(this.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.states = State.getStatesOfCountry(response.paymentMethod.countryCode);

        this.addForm.patchValue(response.paymentMethod);
        this.setFormValidation(response.paymentMethod.paymentType);
      });
      this.addForm.controls["cvv"].clearValidators();
      this.addForm.controls["cvv"].updateValueAndValidity();
  }

  loadSubscriptionsByClientId() {
    this.appService.getAllUserSubscription()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.length) {
          if (this.subscriptionId) {
            this.allSubscriptions = response
            .filter((x: any) => x.id === this.subscriptionId)
            .map((x: any) => {
              let campaignName = x.campaigns[0]?.campaignName;
              if (campaignName) {
                campaignName = campaignName.replace(/ (FB|TT)$/, '');
              }
              x.displayName = `${x.numericId}: ${campaignName} (${x.status})`;
              return x;
            });
          } else {
            this.allSubscriptions = response
              .filter((x: any) => (x.status === 'Active' || x.status === 'Payment Error') && x.campaigns[0]?.clientId === this.addForm.get('clientId')?.value)
              .map((x: any) => {
                let campaignName = x.campaigns[0]?.campaignName;
                if (campaignName) {
                  campaignName = campaignName.replace(/ (FB|TT)$/, '');
                }
                x.displayName = `${x.numericId}: ${campaignName} (${x.status})`;
                return x;
              });
          }
        }
      });
    if (this.subscriptionId) {
      this.appService.fetchAllUserSubscriptions();
    }
  }

  updateData(data: ILead) {
    this.addForm.patchValue(data);
  }

  ngAfterViewInit() {

  }

  onSubmit() {
    this.submitted = true;
    if (this.addForm.invalid) {
      // console.log('Form is invalid');
      return;
    }
    var request = {
      ...this.addForm.getRawValue()
    };

    request = this.trimObjectStrings(request);

    request.id = this.id;
    // console.log('Submitting payment method:', request);
    this.paymentMethodApiService.addEdit(request).subscribe((response: any) => {
      // console.log('Payment method response:', response);
      if (response.good) {
        if (request.subscriptionId) {
          // console.log('Updating subscription with new payment method');
          this.updateSubscriptionPaymentMethod(request.subscriptionId, response.paymentMethod.id);
        } else {
          this.activeModal.close();
          this.oSaveClicked.emit(response);
        }
      }
    });
  }

  updateSubscriptionPaymentMethod(subscriptionId: string, paymentMethodId: string) {
    // console.log('Fetching subscription:', subscriptionId);
    this.subscriptionApiService.getSubscription(subscriptionId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        // console.log('Subscription response:', response);
        if (response.good) {
          const subscription = response.subscription;
          subscription.paymentMethodId = paymentMethodId;
          const request = {
            id: subscription.id,
            adSpend: subscription.products.map((x: any) => { return { productId: x.id, adSpend: x.adSpend } }),
            paymentMethodId: paymentMethodId
          };
          // console.log('Updating subscription:', request);
          this.subscriptionApiService.edit(request).subscribe((editResponse: any) => {
            // console.log('Edit subscription response:', editResponse);
            if (editResponse.good) {
              this.activeModal.close();
              this.oSaveClicked.emit(editResponse);
            }
          });
        }
      });
  }

  // This function will recursively iterate through the object
  trimObjectStrings(obj: any): any {
    // Iterate over each property of the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        
        if (typeof value === 'string') {
          // Trim the string if the value is a string
          obj[key] = value.trim();
        } else if (typeof value === 'object' && value !== null) {
          // Recursively call the function if the value is an object (or array)
          this.trimObjectStrings(value);
        }
      }
    }
    return obj;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
